import { memo, useCallback, useState } from "react";

type TimeValue = {
  hour: number;
  minute: number;
};

type TimePickerProps = {
  value?: TimeValue;
  onChange?: (value: TimeValue) => void;
};

const inputClass =
  "appearance-none no-up-down field-sizing-content w-5 text-center";

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange }) => {
  const [internalTime, setInternalTime] = useState<TimeValue | undefined>();

  // Use controlled or internal value
  const currentTime = value ?? internalTime;

  const updateTime = (newTime: TimeValue) => {
    if (onChange != null) {
      onChange(newTime); // controlled
    } else {
      setInternalTime(newTime); // uncontrolled
    }
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const num = val === "" ? 0 : Math.min(23, Math.max(0, Number(val)));
    updateTime({ minute: currentTime?.minute ?? 0, hour: num });
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const num = val === "" ? 0 : Math.min(59, Math.max(0, Number(val)));
    updateTime({ hour: currentTime?.hour ?? 0, minute: num });
  };

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement, Element>) => {
    e.target.select();
  }, []);

  return (
    <div className="flex flex-row">
      <input
        type="number"
        value={to2Digits(currentTime?.hour, "")}
        onChange={handleHourChange}
        min={0}
        max={23}
        placeholder="--"
        className={inputClass}
        onFocus={handleFocus}
      />
      <span className="flex items-center text-gray-600">:</span>
      <input
        type="number"
        value={to2Digits(currentTime?.minute, "")}
        onChange={handleMinuteChange}
        min={0}
        max={59}
        placeholder="--"
        className={inputClass}
        onFocus={handleFocus}
      />
    </div>
  );
};

function to2Digits(num: number | undefined, defaultValue: string): string {
  if (num == null) {
    return defaultValue;
  }
  if (num < 0 || num > 9) {
    return num.toString();
  }
  return "0" + num.toString();
}

export default memo(TimePicker);
